import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { useEstimatePricingRefresh } from '@/hooks/useEstimatePricingRefresh';
import PricingChangesModal from './PricingChangesModal';
import { toast } from 'sonner';

interface EstimatePricingRefreshButtonProps {
  estimateId: string;
  versionId?: string;
  versionStatus: string;
  versionType?: string;
  companyId?: string;
  onVersionCreated?: (versionId: string) => void;
}

const EstimatePricingRefreshButton: React.FC<EstimatePricingRefreshButtonProps> = ({
  estimateId,
  versionId,
  versionStatus,
  versionType,
  companyId,
  onVersionCreated
}) => {
  const [showModal, setShowModal] = useState(false);
  
  const {
    analyzeVersionPricing,
    pricingChanges,
    hasChanges,
    isAnalyzing,
    clearPricingChanges
  } = useEstimatePricingRefresh({
    estimateId,
    versionId,
    companyId
  });

  // Only show button for sent, approved, or expired versions/change orders
  const shouldShowButton = ['sent', 'approved', 'expired'].includes(versionStatus);
  
  if (!shouldShowButton) {
    return null;
  }

  const handleRefreshClick = async () => {
    const changes = await analyzeVersionPricing();
    if (changes && changes.changes.length > 0) {
      setShowModal(true);
    } else {
      // No changes detected - show success feedback immediately
      toast.success('Pricing is up to date - no changes detected');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    clearPricingChanges();
  };

  const handleVersionCreated = (newVersionId: string) => {
    setShowModal(false);
    clearPricingChanges();
    onVersionCreated?.(newVersionId);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleRefreshClick}
        disabled={isAnalyzing}
        className="flex items-center gap-2"
      >
        <RefreshCw className={`h-4 w-4 ${isAnalyzing ? 'animate-spin' : ''}`} />
        {isAnalyzing ? 'Analyzing...' : 'Refresh Pricing'}
        {hasChanges && (
          <AlertTriangle className="h-4 w-4 text-amber-500" />
        )}
      </Button>

      {showModal && pricingChanges && (
        <PricingChangesModal
          isOpen={showModal}
          onClose={handleModalClose}
          estimateId={estimateId}
          versionId={versionId}
          versionStatus={versionStatus}
          versionType={versionType}
          pricingChanges={pricingChanges}
          onVersionCreated={handleVersionCreated}
        />
      )}
    </>
  );
};

export default EstimatePricingRefreshButton;